import * as braze from '@braze/web-sdk'
import zionDebug from '@fs/zion-debug'
import { register as registerServiceWorker } from '../serviceWorkerRegistration'

const debug = zionDebug('discovery:braze:push')
const SERVICE_WORKER_REGISTRATION_TIMEOUT = 4000

// Helper to build a stable service worker path
const getSWPath = () => `${window?.SERVER_DATA?.appPath || ''}/service-worker.js`

// Memoize the in-flight registration to prevent duplicate dynamic imports/registrations when multiple callers race
let swRegistrationPromise

/**
 * Ensure there is a (ready or pending) service worker registration.
 * Returns the registration or undefined (never throws).
 * Races readiness against a timeout so we don't block permission UI indefinitely.
 */
async function ensureServiceWorkerRegistered() {
  // This is just an extra layer of protection, the service worker registration should have happened long before we
  // ever get here since registration happens in index.js.

  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return undefined
  if (swRegistrationPromise) return swRegistrationPromise

  swRegistrationPromise = (async () => {
    try {
      const existing = await navigator.serviceWorker.getRegistration()
      if (existing) return existing

      // serviceWorkerRegistration already eagerly loaded elsewhere, so just call register
      registerServiceWorker(getSWPath())

      // A small timeout so the permission request flow never hangs if: (a) the SW script 404s or is slow,
      // (b) a browser (Safari / older Android) delays resolving navigator.serviceWorker.ready, or (c)
      // registration is blocked by an extension. If the timeout wins we proceed without a registration
      // (Braze / native logic can retry later). 4s is a sensible upper amount - long enough for first-load
      // install on slower devices, short enough to avoid making a user feel like the UI is frozen.
      const timeout = new Promise((resolve) => {
        // eslint-disable-next-line no-promise-executor-return -- false positive; not returning from a promise executor here
        setTimeout(() => resolve(undefined), SERVICE_WORKER_REGISTRATION_TIMEOUT)
      })
      const registration = await Promise.race([navigator.serviceWorker.ready, timeout])
      return registration
    } catch (e) {
      debug('ensureServiceWorkerRegistered error', e)
      return undefined
    }
  })()

  return swRegistrationPromise
}

export async function requestBrazePushPermission() {
  if (typeof window === 'undefined') return 'default'

  const nativePermission = () => {
    if (Notification?.requestPermission) return Notification.requestPermission()
    return Promise.resolve('default')
  }

  try {
    const brazeFn = braze?.requestPushPermission
    if (typeof brazeFn === 'function') {
      // Ensure SW attempt (non-blocking failure)
      const reg = await ensureServiceWorkerRegistered()
      if (!reg) debug('No service worker registration available prior to Braze permission request')

      try {
        return await new Promise((resolve) => {
          brazeFn((result) => resolve(result ? 'granted' : Notification?.permission || 'denied'), {
            requireExplicitPermission: true,
            serviceWorkerLocation: getSWPath(),
          })
        })
      } catch (invocationErr) {
        debug('error invoking braze.requestPushPermission - falling back', invocationErr)
        return nativePermission()
      }
    }
    return await nativePermission()
  } catch (e) {
    debug('requestBrazePushPermission failed', e)
    return Notification?.permission || 'default'
  }
}
